import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  IndianRupee, 
  Calendar, 
  CheckCircle, 
  Clock, 
  HelpCircle,
  Sliders,
  DollarSign,
  TrendingUp,
  FileText,
  Activity,
  UserCheck
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import { SkipEmiModal, AdjustEmiModal } from './LoanEmiModals';
import styles from './Loans.module.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';
import api from '../../../api/axios';
import { useLoading } from '../../../context/LoadingContext';
import NoData from '../../../components/Common/NoData/NoData';

const MySwal = withReactContent(Swal);

const ViewLoan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loan, setLoan] = useState(null);
  const [summary, setSummary] = useState(null);

  // Modal control states
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [selectedEmiNumber, setSelectedEmiNumber] = useState(null);

  const fetchLoanDetails = async () => {
    try {
      const response = await api.get(`/loans/${id}`);
      if (response.data.success) {
        setLoan(response.data.data);
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching loan details:', error);
      toast.error(error.response?.data?.message || 'Failed to retrieve loan details');
    }
  };

  useEffect(() => {
    fetchLoanDetails();
  }, [id]);

  const handleDoublePayment = async (emiNumber) => {
    const result = await MySwal.fire({
      title: 'Confirm Double Payment',
      text: `Are you sure you want to deduct a double payment starting from EMI #${emiNumber}? This pays EMI #${emiNumber} and the next pending EMI.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, process double payment!'
    });

    if (result.isConfirmed) {
      try {
        await api.post(`/loans/${id}/double-payment`, { emi_number: emiNumber });
        toast.success('Double payment processed successfully');
        fetchLoanDetails();
      } catch (error) {
        console.error('Error double paying:', error);
        toast.error(error.response?.data?.message || 'Failed to process double payment');
      }
    }
  };

  if (!loan) return null;

  const progressPct = summary?.progress_percentage || 0;
  const paidAmt = summary?.paid_amount || 0;
  const remainingAmt = summary?.remaining_amount || 0;

  return (
    <div className={styles['loans-container']}>
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)} />}

      <div className={styles['content-wrapper']}>
        <Header setIsSidebarOpen={setIsSidebarOpen} />

        <main className={styles['main-content']}>
          <Breadcrumbs 
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Loan Requests', path: '/employees/loan-requests' },
              { label: `View ${loan.loan_number}` }
            ]} 
          />

          <div style={{ marginBottom: '24px' }}>
            <button className={styles['control-btn']} onClick={() => navigate('/employees/loan-requests')}>
              <ArrowLeft size={16} />
              <span>Back to List</span>
            </button>
          </div>

          <div className={styles['page-header']} style={{ marginBottom: '24px' }}>
            <div className={styles['header-left']}>
              <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {loan.loan_number}
                <span className={`${styles['status-badge']} ${styles[`status-${loan.status?.toLowerCase()}`]}`} style={{ fontSize: '12px', padding: '4px 12px' }}>
                  {loan.status?.toUpperCase()}
                </span>
              </h1>
              <p>Raised by {loan.employee?.employee_name} ({loan.employee?.employee_code})</p>
            </div>

            {loan.status === 'active' && (
              <button 
                className={[styles['control-btn'], styles['highlight-card']].join(' ')} 
                onClick={() => setIsAdjustModalOpen(true)}
                style={{ borderColor: '#f97316', gap: '8px', color: '#f97316' }}
              >
                <Sliders size={16} />
                <span>Adjust Next EMIs</span>
              </button>
            )}
          </div>

          {/* Recovery analytics widget */}
          {loan.status !== 'pending' && (
            <div className={styles['detail-card']} style={{ padding: '24px' }}>
              <div className={styles['recovery-progress-container']}>
                <div className={styles['progress-header']}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className={styles['info-label']}>Loan Recovery Progress</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                      Recovered ₹{parseFloat(paidAmt).toLocaleString('en-IN')} of ₹{parseFloat(loan.loan_amount).toLocaleString('en-IN')} Principal
                    </span>
                  </div>
                  <span className={styles['progress-pct']}>{progressPct}%</span>
                </div>
                <div className={styles['progress-bar-outer']}>
                  <div className={styles['progress-bar-inner']} style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Grid Layout (General parameter card & Details sidebar) */}
          <div className={styles['details-grid']}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Primary Loan Information */}
              <div className={styles['detail-card']}>
                <h3>Loan Specifications</h3>
                <div className={styles['info-grid']}>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Principal Loan Amount</span>
                    <span className={styles['info-value']} style={{ color: '#0f172a', fontSize: '18px' }}>
                      ₹{parseFloat(loan.loan_amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Interest Rate</span>
                    <span className={styles['info-value']}>{parseFloat(loan.interest_rate || 0)}% APR</span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Monthly EMI Amount</span>
                    <span className={styles['info-value']}>₹{parseFloat(loan.emi_amount || 0).toLocaleString('en-IN')} / month</span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Total Tenure</span>
                    <span className={styles['info-value']}>{loan.total_emis} Months</span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Remaining Tenures</span>
                    <span className={styles['info-value']}>{loan.remaining_emis} months pending</span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Remaining Payable</span>
                    <span className={styles['info-value']} style={{ color: '#ef4444' }}>
                      ₹{parseFloat(remainingAmt).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>First Installment Date</span>
                    <span className={styles['info-value']}>
                      {loan.first_emi_date ? new Date(loan.first_emi_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '---'}
                    </span>
                  </div>
                  <div className={styles['info-item']}>
                    <span className={styles['info-label']}>Repayment Day</span>
                    <span className={styles['info-value']}>Day {loan.emi_day_of_month || 1} of every month</span>
                  </div>
                </div>
              </div>

              {/* Installments Table */}
              <div className={styles['detail-card']} style={{ padding: '24px 0' }}>
                <h3 style={{ padding: '0 24px' }}>Installments & EMIs Schedule</h3>
                
                <div className={styles['custom-table-wrapper']}>
                  <table className={styles['custom-table']}>
                    <thead>
                      <tr>
                        <th style={{ paddingLeft: '24px' }}>EMI #</th>
                        <th>Due Date</th>
                        <th>EMI Amount</th>
                        <th>Repaid Amount</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'center', paddingRight: '24px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loan.emis && loan.emis.length > 0 ? (
                        loan.emis.map((emi) => (
                          <tr key={emi.id}>
                            <td style={{ paddingLeft: '24px', fontWeight: 700 }}>EMI #{emi.emi_number}</td>
                            <td className={styles['date-cell']}>
                              {emi.due_date ? new Date(emi.due_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '---'}
                            </td>
                            <td style={{ fontWeight: 700 }}>₹{parseFloat(emi.emi_amount || 0).toLocaleString('en-IN')}</td>
                            <td>{parseFloat(emi.paid_amount || 0) > 0 ? `₹${parseFloat(emi.paid_amount).toLocaleString('en-IN')}` : '-'}</td>
                            <td>
                              <span className={`${styles['status-badge']} ${styles[`status-${emi.status?.toLowerCase()}`]}`} style={{ minWidth: '80px', padding: '4px 10px', fontSize: '11px' }}>
                                {emi.status?.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ paddingRight: '24px' }}>
                              {emi.status === 'pending' && loan.status === 'active' && (
                                <div className={styles['emi-actions-wrapper']}>
                                  <button 
                                    className={[styles['emi-btn'], styles['skip']].join(' ')} 
                                    onClick={() => { setSelectedEmiNumber(emi.emi_number); setIsSkipModalOpen(true); }}
                                    title="Request to skip this EMI"
                                  >
                                    Skip EMI
                                  </button>
                                  <button 
                                    className={[styles['emi-btn'], styles['double']].join(' ')} 
                                    onClick={() => handleDoublePayment(emi.emi_number)}
                                    title="Process double payment deduction"
                                  >
                                    Double Pay
                                  </button>
                                </div>
                              )}
                              {emi.status !== 'pending' && <span style={{ color: '#94a3b8', fontSize: '12px' }}>No Actions</span>}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ padding: '0' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                              <NoData message="No Installments generated yet. Loan must be approved." isTable={true} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Employee Quick Info Cards */}
              <div className={styles['detail-card']}>
                <h3>Employee Details</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className={styles['avatar-small']} style={{ width: '48px', height: '48px', borderRadius: '12px', fontSize: '16px' }}>
                      {(loan.employee?.employee_name || 'E').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '15px' }}>{loan.employee?.employee_name}</span>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{loan.employee?.employee_code}</span>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '10px', borderTop: '1px dashed #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#64748b', fontWeight: 600 }}>Designation</span>
                      <span style={{ fontWeight: 700, color: '#1e293b' }}>{loan.employee?.designation_name || '---'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#64748b', fontWeight: 600 }}>Department</span>
                      <span style={{ fontWeight: 700, color: '#1e293b' }}>{loan.employee?.department_name || '---'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Justification & Remarks Cards */}
              <div className={styles['detail-card']}>
                <h3>Justification</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <span className={styles['info-label']}>Purpose</span>
                    <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#334155', fontWeight: 500, lineHeight: 1.5 }}>
                      {loan.purpose || 'No purpose documented.'}
                    </p>
                  </div>
                  <div style={{ paddingTop: '12px', borderTop: '1px dashed #f1f5f9' }}>
                    <span className={styles['info-label']}>Applicant Remarks</span>
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#64748b', fontStyle: 'italic', lineHeight: 1.4 }}>
                      "{loan.remarks || 'No applicant remarks.'}"
                    </p>
                  </div>
                </div>
              </div>

              {/* Review & Approver info card */}
              {loan.approved_by && (
                <div className={styles['detail-card']}>
                  <h3>Approval History</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <UserCheck size={18} style={{ color: '#10b981' }} />
                      <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>Approved & Activated</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '28px', borderLeft: '2px solid #ecfdf5' }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        Approved on {loan.approved_at ? new Date(loan.approved_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '---'}
                      </span>
                      {loan.approval_remarks && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#475569', backgroundColor: '#fcfcfc', padding: '10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                          "{loan.approval_remarks}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Transactions Log Section */}
          {loan.transactions && loan.transactions.length > 0 && (
            <div className={styles['detail-card']} style={{ padding: '24px', marginTop: '24px' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 20px 0' }}>
                <Activity size={18} />
                <span>Repayment Activity Logs</span>
              </h3>
              
              <div className={styles['custom-table-wrapper']}>
                <table className={styles['custom-table']}>
                  <thead>
                    <tr>
                      <th>Txn ID</th>
                      <th>Txn Type</th>
                      <th>Method</th>
                      <th>Repaid Amount</th>
                      <th>Repayment Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.transactions.map((txn) => (
                      <tr key={txn.id}>
                        <td style={{ fontWeight: 700 }}>{txn.transaction_number}</td>
                        <td style={{ textTransform: 'capitalize' }}>{txn.transaction_type?.replace('_', ' ')}</td>
                        <td style={{ textTransform: 'capitalize' }}>{txn.payment_method?.replace('_', ' ') || '---'}</td>
                        <td className={styles['amount-cell']}>₹{parseFloat(txn.amount || 0).toLocaleString('en-IN')}</td>
                        <td className={styles['date-cell']}>
                          {txn.transaction_date ? new Date(txn.transaction_date).toLocaleString('en-IN') : '---'}
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${styles['status-approved']}`} style={{ minWidth: '80px', padding: '4px 8px', fontSize: '10px' }}>
                            SUCCESS
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <Footer />
        </main>
      </div>

      <SkipEmiModal 
        isOpen={isSkipModalOpen}
        onClose={() => setIsSkipModalOpen(false)}
        loanId={id}
        emiNumber={selectedEmiNumber}
        onSubmitted={fetchLoanDetails}
      />

      <AdjustEmiModal 
        isOpen={isAdjustModalOpen}
        onClose={() => setIsAdjustModalOpen(false)}
        loanId={id}
        onSubmitted={fetchLoanDetails}
      />
    </div>
  );
};

export default ViewLoan;
